import translate from '@/utils/translate';
import type {OffPlatformCreatorSnapshot} from '@/api/fetchers/crm/offPlatformCreators';
import useBrandCampaignsQuery from '@/hooks/queries/use-brand-campaigns-query';
import useBrandId from '@/hooks/use-brand-id';

type InviteState = Pick<
  OffPlatformCreatorSnapshot,
  'inviteSuppressed' | 'campaignInvites'
>;

export default function useOffPlatformInviteDisabledReason(
  creator: InviteState | undefined,
  {enabled = true}: {enabled?: boolean} = {}
): {disabledReason: string | null; isLoading: boolean} {
  const brandId = useBrandId();
  const {brandCampaigns: campaignsAcceptingProposals, isLoading} =
    useBrandCampaignsQuery({
      brandId,
      states: ['accepting_proposals'],
      enabled: enabled && !!brandId,
    });

  if (!creator) {
    return {disabledReason: null, isLoading};
  }
  if (creator.inviteSuppressed) {
    return {
      disabledReason: translate('This creator opted out of invitation emails'),
      isLoading,
    };
  }

  const invitedCampaignIds = new Set(
    (creator.campaignInvites ?? []).map((invite) => invite.campaignId)
  );
  const campaigns = campaignsAcceptingProposals ?? [];
  const isInvitedToAllCampaigns =
    !isLoading &&
    campaigns.length > 0 &&
    campaigns.every((campaign) => invitedCampaignIds.has(campaign.id));

  return {
    disabledReason: isInvitedToAllCampaigns
      ? translate('Already invited to all campaigns accepting proposals')
      : null,
    isLoading,
  };
}
